// E2E test bridge — exposes a tiny window-side API so Playwright tests can
// exercise selection + time controls + conquest flows without depending on
// 3D raycast pixel projection or button-pixel hits. The canonical .d.ts
// shape lives at apps/app/e2e/test-bridge.d.ts; that file and AodTestBridge
// here MUST stay in sync (TS would catch a divergence on the call sites).
//
// Mounted only when a snapshot is available (the bridge reads from it).

import { useEffect } from 'react';
import type { SimWorld } from '../sim/useSimWorld/index.js';
import type { AttackToast } from '../ui/AttackToastStack.js';
import { buildBridge } from './buildBridge.js';

export type Selection =
  | { kind: 'tile'; id: string }
  | { kind: 'city'; id: string }
  | { kind: 'unit'; id: string }
  | null;

export interface AodTestBridge {
  selectFirstCity: () => string | null;
  selectFirstTile: () => string | null;
  clearSelection: () => void;
  currentSelectionId: () => string | null;
  // Time controls — read state + drive actions.
  currentTick: () => number;
  currentRunState: () => 'running' | 'paused' | 'stopped';
  currentSpeed: () => number;
  pause: () => void;
  resume: () => void;
  setSpeed: (multiplier: number) => void;
  fastForward: (ticks: number) => void;
  // Conquest — drive attacks + read victory.
  attackCity: (cityId: string) => void;
  attackEnemyCity: (count?: number) => string | null;
  currentVictory: () => { winner: number; path: string } | null;
  // New match — reset the world to a fresh demo.
  startNewMatch: (newSeed?: number) => void;
  // Resources — read the local player's current treasury.
  currentTreasury: () => {
    food: number;
    industry: number;
    knowledge: number;
    faith: number;
    gold: number;
  };
  // City HP — read by ownerId so AI-driven defense tests can target the
  // player's own city without first selecting it through the UI.
  cityHpForOwner: (ownerId: number) => { current: number; max: number } | null;
  // Active attack-toast queue (E2E reads this without scraping the DOM).
  attackToasts: () => ReadonlyArray<{
    id: number;
    damage: number;
    hpRemaining: number;
  }>;
  // Units — select, read, and move.
  selectFirstUnit: () => string | null;
  selectFirstUnitByOwner: (ownerId: number) => string | null;
  selectFirstHeroByOwner: (ownerId: number) => string | null;
  unitForOwner: (ownerId: number) => {
    id: string;
    q: number;
    r: number;
    kind: string;
    remainingMovement: number;
    maxMovement: number;
  } | null;
  currentUnitCount: () => number;
  allUnitsDump: () => ReadonlyArray<{
    id: string;
    ownerId: number | undefined;
    q: number | undefined;
    r: number | undefined;
    kind: string | undefined;
    tags: string[] | undefined;
  }>;
  moveHumanUnit: (targetQ: number, targetR: number) => string | null;
  // Heroes — query hero units (tagged "hero" in scene nodes).
  heroForOwner: (ownerId: number) => {
    id: string;
    name: string;
    archetype: string;
    role: string;
    level: number;
    loyalty: number;
    xp: number;
    q: number;
    r: number;
    isWounded: boolean;
    isCaptured: boolean;
    romance?: string;
    rivalry?: string;
    rivalryIntensity?: number;
    inheritedFrom?: string;
  } | null;
  allHeroes: () => ReadonlyArray<{
    id: string;
    name: string;
    archetype: string;
    role: string;
    level: number;
    loyalty: number;
    ownerId: number;
  }>;
  // Garrison — query + deploy.
  garrisonCountForCity: (cityId: string) => number;
  cityCoords: (cityId: string) => { q: number; r: number } | null;
  deployFirstGarrison: (cityId: string) => string | null;
  // Buildings — start construction.
  startBuildingForCity: (cityId: string, buildingId: string) => boolean;
  cityBuildingProgress: (cityId: string) => string | null;
  allBuildings: () => ReadonlyArray<{
    id: string;
    name: string;
    description: string;
    cost: number;
  }>;
  // Combat prediction (U5) — for two units in the scene, returns the
  // predictor's verdict: class counter, formation counter, terrain bonus
  // under the defender, total multiplier, and a favorable/even/unfavorable
  // outcome tag. Returns null if either unit can't be found.
  predictCombatOutcome: (
    attackerUnitId: string,
    defenderUnitId: string,
  ) => {
    classMultiplier: number | null;
    formationMultiplier: number;
    terrainBonus: number;
    totalMultiplier: number;
    outcome: 'favorable' | 'even' | 'unfavorable';
  } | null;
}

interface UseTestBridgeOptions {
  readonly sim: SimWorld;
  readonly localPlayerIndex: number;
  readonly selection: Selection;
  readonly setSelection: (s: Selection) => void;
  readonly attackToasts: ReadonlyArray<AttackToast>;
  /** False leaves `window.__aodTest` to whoever owns the live sim (default true). */
  readonly enabled?: boolean;
}

export function useTestBridge(options: UseTestBridgeOptions): void {
  const { sim, localPlayerIndex, selection, setSelection, attackToasts, enabled = true } = options;
  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || !sim.snapshot) return;
    const bridge = buildBridge({
      snapshot: sim.snapshot,
      sim,
      localPlayerIndex,
      selection,
      setSelection,
      attackToasts,
    });
    (window as unknown as { __aodTest?: AodTestBridge }).__aodTest = bridge;
  }, [enabled, sim, localPlayerIndex, selection, setSelection, attackToasts]);
}
